import type { Dispatch, SetStateAction, SyntheticEvent } from 'react'
import React, { useEffect, useState } from 'react'

import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { useCustomer } from 'hooks/useCustomer'
import _ from 'lodash'

import type { MstCustomer } from 'api-client'
import type { AutocompleteCustomer } from 'hooks/useCustomer'

interface SelectCustomerProps {
  trCompanyId: number | null | undefined
  orgCustomerId: string | null | undefined
  setOrgCustomerId: Dispatch<SetStateAction<string | null | undefined>> 
  setCustomerInfo: Dispatch<SetStateAction<MstCustomer | undefined>>
  isRequired: boolean
  setIsValid: Dispatch<SetStateAction<boolean>>
  enabled?: boolean
}

function SelectCustomer({
  trCompanyId,
  orgCustomerId,
  setOrgCustomerId,
  setCustomerInfo,
  isRequired,
  setIsValid,
  enabled,
}: SelectCustomerProps) {
  const { loginUserInfo } = useAuthContext()

  const { customers, autocompletCustomers, fetchCustomer, isLoading } = useCustomer()

  const [trCustomer, setTrCustomer] = useState<AutocompleteCustomer[]>([])
  const [trValue, setTrValue] = useState<AutocompleteCustomer | undefined | null>(undefined)
  const [isFirstRender, setIsFirstRender] = useState(true)

  useEffect(() => {
    // 初回レンダリング以外はtrCompanyIdが変わることにクリアにする
    if (!isFirstRender) {
      if (setOrgCustomerId){
        setTrValue(undefined)
        setOrgCustomerId(undefined)
      }
    }

    if (trCompanyId === undefined || trCompanyId === null || trCompanyId === loginUserInfo?.companyId) {
      fetchCustomer({ enabled: enabled ?? undefined, companyId: loginUserInfo!.companyId }, false)
    } else {
      // hooksを呼び出して直後にセッターに入れても、すぐに更新されないため別のuseStateで監視する
      fetchCustomer({ enabled: enabled ?? undefined, companyId: trCompanyId }, false)
    }
  }, [trCompanyId])

  useEffect(() => {
    // センターAPIが呼び出されレスポンスが返ってきた場合のみ実行
    if (customers.list.length) {
      setTrCustomer(autocompletCustomers);
      setTrValue(_.find(autocompletCustomers, { orgCustomerId }))
    }

    if (isFirstRender) {
      setIsFirstRender(false)
    }
  }, [customers])

  // 必須チェック処理
  useEffect(() => {
    // 必須チェックを上位コンポーネントに通知
    if (trValue) {
      setIsValid(false)
    } else {
      setIsValid(true)
    }
  }, [trValue])


  return (
    <FormControl>
      <Autocomplete
        disablePortal
        id="customer"
        // valueを明示的に指定(controlled component)しなければならないが、未指定の場合はundefinedが返るので
        // undefinedの場合はnullを指定する
        value={trValue ?? null}
        options={trCustomer}
        loading={isLoading}
        // fetch中はdisabledにする
        disabled={isLoading}
        onChange={(e: SyntheticEvent<Element, Event>, selectedValue: AutocompleteCustomer | null) => {
          // センター情報を上位コンポーネントに通知
            setOrgCustomerId(selectedValue?.orgCustomerId)
            setCustomerInfo(_.find(customers.list, { orgCustomerId: selectedValue?.orgCustomerId }))

            setTrValue(selectedValue)
        }}
        isOptionEqualToValue={(option, value) => option.orgCustomerId === value.orgCustomerId}
        renderInput={(params) => (
          <TextField
            label="得意先"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <div>{isLoading ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment}</div>
              ),
            }}
            // 必須入力チェック関するprops
            error={isRequired && !trValue}
            helperText={isRequired && !trValue && '得意先'}
            required={isRequired}
          />
        )}
      />
    </FormControl>
  )
}

export default SelectCustomer
